module Meevo.Plugins.ActivityLog {

    import DiscountLine = Meevo.Model.DiscountLine;
    import SalesTypeEnum = Meevo.Model.SalesTypeEnum;
    import PresetStatus = Meevo.Components.Controls.PresetStatus;
    import Sale = Meevo.Model.Sale;
    import SaleLine = Meevo.Model.SaleLine;
    import Employee = Meevo.Model.Employee;
    import NounEnum = Meevo.Model.NounEnum;
    import NounClassEnum = Meevo.Model.NounClassEnum;

    export enum CategorySearchEnum {
        All = 0,
        Appointment,
        Clients,
        SecurityAudit,
        DataChanges,
        Transaction,
        Payroll,
        PayrollSettings,
        PayrollData,
        AllPayroll,
        ReportScheduler,
        Communications,
        GiftCards
    }

    export enum SecurityAuditCriteriaEnum {
        AccessAttemptsElevations,
        SecuritySettingChanges,
        SecurityRoleSettingsUsers,
        SecurityRoleModifications
    }

    export enum TimeTypesEnum {
        Custom = 0,
        Morning,
        Afternoon,
        Evening,
        FullDay
    }

    export interface IDateRangeParams {
        minDate: Date;
        presetList: IPresetItem[],
        presetListOneWeek: IPresetItem[],
        selectedPreset: IPresetItem;
        predefinedPresetStatus: PresetStatus;
        predefinedPresetStatusEmployee: PresetStatus;
        useCustomError: boolean;
    }

    export interface IPresetItem {
        name: string;
        status: PresetStatus;
    }

    export interface IActivityElement {
        application: string;
        dateTime: Date;
        description: string;
        entityId?: Guid;
        id: Guid;
        internalSessionId: Guid;
        isTransaction: boolean;
        locationId: number;
        nounClassId: NounEnum;
        nounId: NounClassEnum;
        tenantId: number;
        userId: string;
    }

    export interface ITransactionInfo extends Sale {
        generalizedData: IActivityElement;
        hasStudentTeacherModuleTurnOn: boolean;
        hasSplitCommission: boolean;
        isHasGiftCard?: boolean;
        isHasRefund?: boolean;
        totalRefunded?: number;
        saleLines: IPreparedSaleLine[];
        uniquePayments?: any;
    }

    interface IPreparedSaleLine extends SaleLine {
        uniqueDiscounts?: DiscountLine[];
    }

    interface IChangeItems {
        fieldName: string;
        newValue: any;
        oldValue?: any;
    }

    interface IAffiliationList {
        [key: number]: NounClassEnum[] | NounEnum[];
    }  

    interface IExpandModalConfig {
        isHideOldValueColumn?: boolean;
    }

    interface IElemntAndIndexArrayResults {
        indexOfElement: any,
        element: any
    }

    export interface IActivityLogService {
        getNewSelectedPresetStatus: (dateRangeParams: IDateRangeParams, presetList: IPresetItem[], onlyOneWeekAvailable: boolean) => IPresetItem;
        getAuditDetailedInformation: (element: IActivityElement, auditActivityType: CategorySearchEnum, pagination: IPagination) => any;
        getTransactionDetailedInformation: (element: IActivityElement) => any;
        getAuditDetailsPreApi: (element: Partial<IActivityElement>, pagination: IPagination) => ng.IPromise<any>;
        getAuditDataApi: (element: Partial<IActivityElement>, pagination: IPagination) => ng.IPromise<any>;
        getAuditDetailCount: (element: Partial<IActivityElement>, pagination: IPagination) => ng.IPromise<number>;
        getTypeOfActivity: (element: IActivityElement) => CategorySearchEnum;
        getExpandModalConfig: (element: IActivityElement) => IExpandModalConfig;
        isPayrollActivity: (activity: CategorySearchEnum) => boolean;
        hasSplitCommission: (saleLine: SaleLine) => boolean;
        getFilteredArrayResults: (searchText: string, arrayResultsAllItems: Array<any>) => Array<any>;
        getElementAndIndexInArrayResults: (itemId: string, arrayResultsAllItems: Array<any>) => IElemntAndIndexArrayResults;
        getIndexInFilteredArrayResults: (filteredArrayResults: Array<any>, elementScrollToId: any) => number;
        getCurrentEmployee: () => ng.IPromise<any>;
        count: number;
    }

    export interface IPagination {
        pageNumber: number,
        itemsPerPage: number
    }

    export class ActivityLogService implements IActivityLogService {
        private basicActivityAffiliationList: IAffiliationList = {
            [CategorySearchEnum.Appointment]: [
                NounClassEnum.AppointmentCategory,
                NounClassEnum.Appointment,
                NounClassEnum.AppointmentBook
            ],
            [CategorySearchEnum.Clients]: [
                NounClassEnum.Client,
                NounClassEnum.ClientFind,
                NounClassEnum.ClientGiftCard,
                NounClassEnum.ClientPointManager,
                NounClassEnum.ClientType,
                NounClassEnum.ClientVisitNote
            ],
            [CategorySearchEnum.SecurityAudit]: [
                NounClassEnum.Security,
                NounClassEnum.SecurityElevation,
                NounClassEnum.SecurityRoleManager
            ],
            [CategorySearchEnum.ReportScheduler]: [
                NounClassEnum.ReportScheduler
            ]
        };

        private payrollAffiliationList: IAffiliationList = {
            [CategorySearchEnum.Payroll]: [
                NounEnum.Payroll,
                NounEnum.PayrollPage
            ],
            [CategorySearchEnum.PayrollData]: [
                NounEnum.EmployeeCommissionOverrides,
                NounEnum.EmployeeCompensation,
                NounEnum.EmployeeDeduction,
                NounEnum.EmployeeDeductions,
                NounEnum.ProductCompensation,
                NounEnum.ServiceCompensation,
                NounEnum.StudentCompensation,
                NounEnum.TeacherCompensation,
                NounEnum.StudentCompensations,
                NounEnum.TeacherCompensations,
                NounEnum.MembershipCompensation
            ],
            [CategorySearchEnum.PayrollSettings]: [
                NounEnum.PayrollSettings,
                NounEnum.PayrollSettingsCommissionDetail,
                NounEnum.PayrollSettingsSplitCommissionSetting,
                NounEnum.PayrollSettingsMain,
                NounEnum.CompensationSettings
            ]
        };
        private _count: number;

        static $inject = [
            'meevo.components.common.IdentityService',
            'meevo.components.common.DataService',
            'meevo.components.common.SecurityService',
            'meevo.AppSettings',
            '$translate',
            '$q'
        ];

        constructor(
            protected identityService: Meevo.Components.Common.IIdentityService,
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected securityChecker: Meevo.Components.Common.ISecurityService,
            protected appSettings: Meevo.Components.Common.IAppSettings,
            protected $translate: angular.translate.ITranslateService,
            protected $q: ng.IQService
        ) { }

        /**
         * Retrieve detailed information about audit for using it in modal window
         * @param {IActivityElement} element
         * @param {CategorySearchEnum} auditActivityType
         * @param {CategorySearchEnum} auditActivityType
         * @param {IPagination} pagination
         * @returns {ng.IPromise<any>} promise
         */
        getAuditDetailedInformation(element: IActivityElement, auditActivityType: CategorySearchEnum, pagination: IPagination) {
            let modalScope = [];

            let auditDeatilPromise = this.getAuditDetailsPreApi(element, pagination).then(auditDetails => {
                auditDetails.changeItems = this.preparedChangeItems(auditDetails.changeItems);
                modalScope['auditDetails'] = auditDetails;
                modalScope['auditDetails']['generalizedData'] = element;
                return this.getAdditionInformation(modalScope['auditDetails'].nounId, element, auditActivityType);
            }).then((additionInformation: any) => {
                if (auditActivityType === CategorySearchEnum.DataChanges || this.isPayrollActivity(auditActivityType)) _.extend(modalScope['auditDetails'], additionInformation);

                // Todo: refactoring after demo?
                if (additionInformation && additionInformation.client.guidId) {
                    modalScope['auditDetails'].description = modalScope['auditDetails'].description + ' - ' + additionInformation.client.displayName;
                }
                else if (modalScope['auditDetails'].clientName) {
                    modalScope['auditDetails'].description = modalScope['auditDetails'].description + ' - ' + modalScope['auditDetails'].clientName;
                }

                return modalScope;
            });

            return auditDeatilPromise;
        }

        getAuditDetailsPreApi(element: Partial<IActivityElement>, pagination: IPagination): ng.IPromise<any> {
            return this.getAuditDetailCount(element, pagination).then(count => {
                this._count = count;
                return this.getAuditDetails(this.getRequestBody(element, pagination));
            })
        };

        getAuditDetailCount(element: Partial<IActivityElement>, pagination: IPagination): ng.IPromise<number> {
            return this.getAuditDetailsCountApi(this.getRequestBody(element, pagination));
        };

        getAuditDataApi(element: Partial<IActivityElement>, pagination: IPagination): ng.IPromise<any> {
            return this.getAuditDetails(this.getRequestBody(element, pagination));
        };

        getRequestBody(element, pagination): any {
           return {
                internalSessionId: element.internalSessionId,
                auditHeaderId: element.id,
                entityId: element.entityId ? element.entityId : null,
                ...pagination
            };
        };

        /**
         * Retrieve detailed information about transaction for using it in modal window
         * @param {IActivityElement} element
         * @returns {ng.IPromise<any>} promise
         */
        getTransactionDetailedInformation(element: IActivityElement): ng.IPromise<any> {
            return this.getTransactionDetails(element.id).then((result: Sale) => {
                return { transactionItem: this.preparedTransactionInfo(element, result) };
            });
        }

        /**
         * Get selected preset status after swithing from All employee or Employee selected modes in filters
         * @param {IDateRangeParams} dateRangeParams
         * @param {IPresetItem[]} presetList
         * @param {boolean} onlyOneWeekAvailable
         * @returns {IPresetItem}
         */
        getNewSelectedPresetStatus(dateRangeParams: IDateRangeParams, presetList: IPresetItem[], onlyOneWeekAvailable: boolean): IPresetItem {
            const defaultPreset = onlyOneWeekAvailable ? dateRangeParams.predefinedPresetStatusEmployee : dateRangeParams.predefinedPresetStatus;

            const newSelectedPreset: IPresetItem = _.find(presetList, (preset: IPresetItem) => preset.status === dateRangeParams.selectedPreset.status);
            const defaultSelectedPreset: IPresetItem = _.find(presetList, (preset: IPresetItem) => preset.status === defaultPreset);

            return newSelectedPreset || defaultSelectedPreset;
        }

        /**
         * Get activity type for a specific element
         * @param {IActivityElement} element
         * @returns {CategorySearchEnum}
         */
        getTypeOfActivity(element: IActivityElement): CategorySearchEnum {
            if (element.isTransaction) return CategorySearchEnum.Transaction;

            // Default value
            const activityType: CategorySearchEnum = CategorySearchEnum.DataChanges;

            // Check for payroll activity and then for other
            return this.findActivityInListByNoun(element.nounId, this.payrollAffiliationList) ||
                this.findActivityInListByNoun(element.nounClassId, this.basicActivityAffiliationList) ||
                activityType;
        } 

        /**
         * Get custom config for expamd modal
         * @param {IActivityElement} element
         * @returns {IExpandModalConfig} 
         */
        getExpandModalConfig(element): IExpandModalConfig {
            let config = <IExpandModalConfig>{};

            if (element.nounId === NounEnum.Sale) config.isHideOldValueColumn = true;

            return config;
        }

        /**
         * Check if activity related to Payroll
         * @param {CategorySearchEnum} activity
         * @returns {boolean}
         */
        isPayrollActivity(activity: CategorySearchEnum): boolean {
            return _.contains([CategorySearchEnum.AllPayroll,
                CategorySearchEnum.Payroll,
                CategorySearchEnum.PayrollData,
                CategorySearchEnum.PayrollSettings], activity);
        }

        /**
         * Check if saleLine has split commission
         * @param {SaleLine} saleLine
         * @returns {boolean}
         */
        hasSplitCommission(saleLine): boolean {
            // secondEmployee not null, secondEmployeeId null when adding new second employee.  Typeahead is empty
            // secondEmployee null, secondEmployeeId not null when clearing typeahead
            return !!(saleLine.secondEmployee || saleLine.secondEmployeeId);
        }

        /**
         * Removed duplicate and properties with guid
         * @param {IChangeItems[]} changeItems
         * @returns {IChangeItems[]}
         */
        private preparedChangeItems(changeItems: IChangeItems[]) {
            // Removing duplicate property
            changeItems = _.uniq(changeItems, (changeItem: IChangeItems) => {
                return [changeItem.fieldName, changeItem.newValue, changeItem.oldValue].join(',');
            });

            // Removing property with guid
            let regexp = new RegExp("[\\d\\w]{8}-[\\d\\w]{4}-[\\d\\w]{4}-[\\d\\w]{4}-[\\d\\w]{12}");
            changeItems.filter((changeItem: IChangeItems) => !regexp.test(changeItem.newValue) && !regexp.test(changeItem.oldValue));

            return changeItems;
        }

        /**
         * Filter arrayResults by needed string
         * @param {string} searchText
         * @param {Array<any>} arrayResultsAllItems
         * @returns {Array<any>}
         */
        getFilteredArrayResults(searchText: string, arrayResultsAllItems: Array<any>): Array<any> {
            if (!searchText || !arrayResultsAllItems) {
                return;
            }

            return arrayResultsAllItems.filter(activityItem => {
                if (activityItem && activityItem.description.toLowerCase().search(searchText.toLowerCase()) > -1) return activityItem;
            });
        }

        /**
         * Get element and index it in the arrayResults all items by index from getFilteredArrayResults()
         * @param {string} itemId
         * @param {Array<any>} arrayResultsAllItems
         * @returns {IElemntAndIndexArrayResults}
         */
        getElementAndIndexInArrayResults(itemId: string, arrayResultsAllItems: Array<any>): IElemntAndIndexArrayResults {
            return {
                indexOfElement: arrayResultsAllItems.findIndex(elem => elem.id === itemId),
                element: arrayResultsAllItems.find(elem => elem.id === itemId)
            }
        }

        /**
         * Return index of element in filteredArrayResults by elementScrollToId from arrayResults.
         * @param {Array<any>} filteredArrayResults
         * @param {any} elementScrollToId
         * @returns {number}
         */
        getIndexInFilteredArrayResults(filteredArrayResults: Array<any>, elementScrollToId: any): number {
            return filteredArrayResults.findIndex(elem => elem.id == elementScrollToId);
        }

        /**
         * Return object of employee for reset btn logic
         * @returns {ng.IPromise<any>} promise
         */
        getCurrentEmployee(): ng.IPromise<any> {
            const currentUser = this.identityService.current();

            return this.getEmployeeById(currentUser.userId).then((emp: Employee) => {
                return {
                    id: emp.id,
                    displayName: emp.isLocationEmployee ? emp.displayName : currentUser.displayName
                };
            })
        }

        /**
         * Modify transaction item and add additional properties
         * @param {IActivityElement} element
         * @param {Sale} transactionItem
         * @returns {any}
         */
        private preparedTransactionInfo(element: IActivityElement, transactionItem: Sale): ITransactionInfo {
            const preparedInfo: ITransactionInfo = Object.assign(transactionItem);

            preparedInfo.hasStudentTeacherModuleTurnOn = this.securityChecker.hasSecurityDenyOrGrant(
                Meevo.Components.Common.SecurityKeys.DATA_STUDENTTEACHERMODULE + Meevo.Components.Common.SecurityParts.KEY_PART_READ
            );
            preparedInfo.generalizedData = element;
            preparedInfo.hasSplitCommission = this.itemHasSplitCommission(transactionItem);

            preparedInfo.totalRefunded = 0;
            preparedInfo.saleLines.forEach((saleLine: IPreparedSaleLine) => {
                if (saleLine.isRefund) {
                    preparedInfo.isHasRefund = true;
                    preparedInfo.totalRefunded += Math.abs(saleLine.finalPrice);
                };

                if (saleLine.saleType === SalesTypeEnum.GiftCard) preparedInfo.isHasGiftCard = true;

                // Take unique discounts by type for modal and CSV
                if (saleLine.discountLines && saleLine.discountLines.length) {
                    saleLine.uniqueDiscounts = _.uniq(saleLine.discountLines, (discountLine: DiscountLine) => discountLine.type);
                }
            });

            if (preparedInfo.paymentLines && preparedInfo.paymentLines.length) {
                preparedInfo.uniquePayments = _.uniq(preparedInfo.paymentLines, (paymentLine: any) => paymentLine.paymentTypeDisplayName);
            }

            return preparedInfo;
        }

        /**
         * Retrieve additional information for different types of audit
         * @param {NounEnum} nounId
         * @param {IActivityElement} element
         * @param {CategorySearchEnum} auditActivityType
         * @returns {ng.IPromise<any>} promise
         */
        private getAdditionInformation(nounId: NounEnum, element: IActivityElement, auditActivityType: CategorySearchEnum): ng.IPromise<any> {
            let additionInformation = this.$q.resolve();
            //switch (auditActivityType) {
            //    case CategorySearchEnum.DataChanges:
            //        // Get client info CSV file
            //        if (angular.isDefined(element.entityId)) {
            //            additionInformation = this.getDataChangesAdditionInformation(nounId, element.entityId);
            //        }
            //        break;
            //}

            return this.getDataChangesAdditionInformation(nounId, element.entityId);

            //return additionInformation;
        }

        /**
         * Retrieve additional information for Data Changes activity
         * @param {any} nounId
         * @param {any} entityId
         * @returns {ng.IPromise<any>} promise
         */
        private getDataChangesAdditionInformation(nounId, entityId): ng.IPromise<any> {
            return this.getEntityDisplayName({
                noun: nounId,
                entityIds: [entityId]
            }).then(result => {
                let client = (function getClientInfo(clientInfo) {
                    if (clientInfo) return clientInfo;
                    return nounId === 319 ? { displayName: 'Transaction Updated' } : { displayName: '' }
                })(result);

                return { client };
            });
        }

        /**
         * Get connection using id
         * @param {any} applicationConnectionId
         * @returns {ng.IPromise<any>} promise
         */
        private getAuditDetails(criteria: any): ng.IPromise<any> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/ActivityLog/AuditDetails`, 'POST', criteria)
                .then(response => response.data)
        }

        /**
         * Get connection using id
         * @param {any} applicationConnectionId
         * @returns {ng.IPromise<any>} promise
         */
        private getAuditDetailsCountApi(criteria: any): ng.IPromise<any> {
            return this.dataService.customMethodData(`/${this.appSettings.API_ROOT}/ActivityLog/AuditDetails/Count`, 'POST', criteria)
                .then(response => response.data)
        }

        /**
         * Get entity display name for csv file
         * @param {any} applicationConnectionId
         * @returns {ng.IPromise<any>} promise
         */
        private getEntityDisplayName(criteria: any): ng.IPromise<any> {
            return this.dataService.customMethodData('api/ActivityLog/DataChange/EntityDisplayName', 'POST', criteria)
                .then(response => response.data[0]);
        }

        /**
         * Get employee by id
         * @param {Guid} employeeId
         * @returns {ng.IPromise<string>} promise
         */
        private getEmployeeById(employeeId: Guid): ng.IPromise<Employee> {
            return this.dataService.customMethodData(`api/employee/${employeeId}`, 'GET', null)
                .then(response => response.data);
        }

        /**
         * Get detailed information about transaction for modal
         * @param {Guid} transactionId
         * @returns {ng.IPromise<Sale>} promise
         */
        private getTransactionDetails(transactionId: Guid): ng.IPromise<Sale> {
            return this.dataService.customMethodData(`api/sale/${transactionId}`, 'GET', null, {
                locale: this.appSettings.APP_COUNTRYID
            }).then(result => result.data)
        }

        /**
         * Find activity by noun in a specific list
         * @param {NounEnum | NounClassEnum} noun
         * @param {IAffiliationList} listForSearching
         * @returns {CategorySearchEnum}
         */
        private findActivityInListByNoun(noun: NounEnum | NounClassEnum, listForSearching: IAffiliationList): CategorySearchEnum {
            let result: CategorySearchEnum;

            for (let activity in listForSearching) {
                if (!listForSearching.hasOwnProperty(activity)) return;

                if (_.contains(listForSearching[activity], noun)) {
                    result = parseInt(activity, 10);
                    break;
                }
            }

            return result;
        }

        /**
         * Check if transaction has a plit commission
         * @param {Sale} transItem
         * @returns {boolean}
         */
        private itemHasSplitCommission(transItem: Sale): boolean {
            return !!_(transItem.saleLines).find((saleLine) => saleLine.secondEmployee != null || saleLine.secondEmployeeId != null);
        }

        get count(): number {
            return this._count;
        }
    }

    angular.module('meevo.plugins.activityLog')
        .service('meevo.plugins.activityLog.ActivityLogService', ActivityLogService);
}
